import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ModalLoading from "../../../../components/Formularios/Modal/ModalLoading";

import { useForm } from "react-hook-form";
import { TicketInterface } from "../../../../types/TicketInterface";
import { endpointPrincipal } from "../../../../api/endPoints/endpointPrincipal/endpointPrincipal";
import ModalAtividade from "../modalAtividadeTicket";
import { getSessionData } from "../../../../utils/storageUtils";
import FormTitulo from "../../../../components/Formularios/Container/FormTitulo";
import FormActions from "../../../../components/Formularios/Buttons/FormActions";
import CustomTextField from "../../../../components/Formularios/Inputs/CustomTextField";
import CustomSelect from "../../../../components/Formularios/Inputs/CustomSelect";
import ActionToolbar from "../../../../components/ActionToolbar/ActionToolbar";
import CustomTable, { ColunaTabela } from "../../../../components/Formularios/Table/CustomTable";
import usePaginacaoLocal from "../../../../hooks/usePaginacaoLocal";

interface FormTicketProps {
  idticket?: string;
  acao?: string;
}

// Função para formatar a data no formato dd/mm/aaaa hh:mm:ss
const formatarData = (data: string) => {
  if (!data) return "";

  try {
    const dataObj = new Date(data);

    // Verificar se a data é válida
    if (isNaN(dataObj.getTime())) {
      return data; // Retorna o valor original se a data for inválida
    }

    // Formatar dia, mês e ano
    const dia = dataObj.getDate().toString().padStart(2, "0");
    const mes = (dataObj.getMonth() + 1).toString().padStart(2, "0");
    const ano = dataObj.getFullYear();

    // Formatar hora, minuto e segundo
    const hora = dataObj.getHours().toString().padStart(2, "0");
    const minuto = dataObj.getMinutes().toString().padStart(2, "0");
    const segundo = dataObj.getSeconds().toString().padStart(2, "0");

    return `${dia}/${mes}/${ano} ${hora}:${minuto}:${segundo}`;
  } catch (error) {
    console.error("Erro ao formatar data:", error);
    return data; // Retorna o valor original em caso de erro
  }
};

const colunas: ColunaTabela[] = [
  {
    key: "createdAt",
    label: "Data",
    render: (createdAt) => formatarData(createdAt),
  },
  { key: "descricao", label: "Descrição" },
  {
    key: "usuario",
    label: "Usuário",
    render: (usuario) => usuario?.nome || "Não informado",
  },
  {
    key: "arquivos",
    label: "Arquivos",
    render: (arquivos) => {
      if (!arquivos || arquivos.length === 0) {
        return "Nenhum arquivo";
      }
      return `${arquivos.length} arquivo(s)`;
    },
  },
];

// Converte as opções do código do sistema para o formato do select
const paraOpcoes = (lista: any[]) =>
  lista.map((item) => ({ valor: item.codigo, label: item.descricao }));

const FormTicket = ({ idticket, acao }: FormTicketProps) => {
  const { register, handleSubmit, setValue, control, getValues } =
    useForm<TicketInterface>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [agenteOptions, setAgenteOptions] = useState<
    { id_agente: number; nome: string }[]
  >([]);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [prioridadeOptions, setPrioridadeOptions] = useState<any[]>([]);
  const [gruposOptions, setGruposOptions] = useState<any[]>([]);
  const [tipoOptions, setTipoOptions] = useState<any[]>([]);
  const [atividades, setAtividades] = useState<any[]>([]);
  const [showModalAtividade, setShowModalAtividade] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState<string>("");
  const [perfilUsuario, setPerfilUsuario] = useState<string>("");
  const [dadosAtividade, setDadosAtividade] = useState({
    acao: "Novo",
    descricao: "",
    ticketData: {} as TicketInterface,
    isNewTicket: false,
    id: 0,
  });

  const paginacaoAtividades = usePaginacaoLocal(atividades, 5);

  // Define se está no modo de visualização
  const isVisualizando = acao === 'Visualizar';

  const carregarOpcoesSelect = useCallback(async () => {
    try {
      const [
        statusResponse,
        prioridadesResponse,
        gruposResponse,
        agentesResponse,
        tiposResponse,
      ] = await Promise.all([
        endpointPrincipal.get("codigosistema/detalhes/STATUS"),
        endpointPrincipal.get("codigosistema/detalhes/PRIORIDADES"),
        endpointPrincipal.get("codigosistema/detalhes/GRUPOS"),
        endpointPrincipal.get("tickets/agentes"),
        endpointPrincipal.get("codigosistema/detalhes/TIPO"),
      ]);

      setStatusOptions(statusResponse.data);
      setPrioridadeOptions(prioridadesResponse.data);
      setGruposOptions(gruposResponse.data);
      setAgenteOptions(agentesResponse.data);
      setTipoOptions(tiposResponse.data);

      // Se for um novo ticket, definir valores padrão após carregar as opções
      if (idticket === "novo") {
        // Encontrar o código do status "Pendente"
        const statusPendente = statusResponse.data.find(
          (status: any) => status.descricao.toUpperCase() === "PENDENTE"
        );

        // Encontrar o código da prioridade "Baixa"
        const prioridadeBaixa = prioridadesResponse.data.find(
          (prioridade: any) => prioridade.descricao.toUpperCase() === "BAIXA"
        );

        const tipoOutros = tiposResponse.data.find(
          (tipo: any) => tipo.descricao.toUpperCase() === "OUTROS"
        );

        if (statusPendente) {
          setValue("status", statusPendente.codigo);
        }

        if (prioridadeBaixa) {
          setValue("prioridade", prioridadeBaixa.codigo);
        }

        if (tipoOutros) {
          setValue("tipo", tipoOutros.codigo);
        }
      }
    } catch (error) {
      toast.error("Falha ao carregar opções dos campos. Motivo: " + error);
    }
  }, [idticket, setValue]);

  const carregarTicketPorId = useCallback(async () => {
    setLoading(true);
    try {
      await carregarOpcoesSelect();

      const { data } = await endpointPrincipal.get(
        `tickets/${idticket}/atividades`
      );
      setValue("id", data.id_ticket);
      setNomeUsuario(data.contato);
      setValue("contato", data.contato);
      setValue("assunto", data.titulo);
      setValue("agente_id", data.agente_id);
      setValue("tipo", data.tipo);
      setValue("status", data.status);
      setValue("prioridade", data.prioridade);
      setValue("grupo", data.grupo);
      setValue("dataVencimento", data.dataVencimento ? new Date(data.dataVencimento).toLocaleString('sv').replace(' ', 'T').slice(0, 16) : "");
      setAtividades(data.atividades || []);
    } catch (error) {
      toast.error("Falha ao carregar dados. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  }, [idticket, carregarOpcoesSelect, setValue]);

  useEffect(() => {
    // Obter o nome e perfil do usuário do localStorage usando a função utilitária
    const nomeUsuarioStorage = getSessionData("NomeUsuario") || "";
    const perfilUsuarioStorage = getSessionData("PerfilUsuario") || "";
    setNomeUsuario(nomeUsuarioStorage);
    setPerfilUsuario(perfilUsuarioStorage);

    if (idticket && idticket !== "novo") {
      carregarTicketPorId();
    } else {
      // Se for um novo ticket, preencher o campo contato com o nome do usuário
      setValue("contato", nomeUsuarioStorage);
      // Definir valores padrão para status e prioridade
      setValue("status", "PENDENTE");
      setValue("prioridade", "BAIXA");
      carregarOpcoesSelect();
    }
  }, [idticket, carregarTicketPorId, carregarOpcoesSelect, setValue]);

  const onSubmit = async (data: TicketInterface) => {
    setLoading(true);
    try {
      if (idticket === "novo") {
        const formData = new FormData();

        // Adicionar os campos de texto
        formData.append("titulo", data.assunto || "");
        formData.append("contato", nomeUsuario);
        formData.append("agente_id", data.agente_id?.toString() || "0");
        formData.append("tipo", data.tipo || "");
        formData.append("status", data.status || "");
        formData.append("prioridade", data.prioridade || "");
        formData.append("grupo", data.grupo || "");
        formData.append(
          "dataVencimento",
          data.dataVencimento ? new Date(data.dataVencimento).toISOString() : new Date().toISOString()
        );
        formData.append(
          "descricao",
          atividades.length > 0 ? atividades[0].descricao : "Ticket criado"
        );

        // Adicionar os arquivos da primeira atividade
        if (atividades.length > 0 && atividades[0].arquivos) {
          for (const arquivo of atividades[0].arquivos) {
            formData.append("arquivos", arquivo);
          }
        }

        await endpointPrincipal.post("tickets", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Ticket criado com sucesso");
        // Navegar para a lista de tickets após sucesso
        setTimeout(() => navigate("/ticket"), 2000);
      } else {
        const ticketData = {
          titulo: data.assunto,
          tipo: getValues("tipo") || data.tipo,
          grupo: data.grupo,
          status: data.status,
          prioridade: data.prioridade,
          agente_id: data.agente_id || null,
          dataVencimento: data.dataVencimento ? new Date(data.dataVencimento).toISOString() : new Date().toISOString(),
        };

        await endpointPrincipal.patch(`tickets/${idticket}`, ticketData);
        toast.success("Ticket atualizado com sucesso");
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error: any) {
      toast.error(
        `Erro ao ${idticket === "novo" ? "criar" : "atualizar"} ticket: ${
          error.response?.data?.message || "Erro desconhecido"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (idticket === "novo") {
      // Obter os valores do formulário
      const titulo = getValues("titulo") || getValues("assunto");

      if (!titulo) {
        toast.error("O assunto do ticket é obrigatório");
        return;
      }

      // Se estiver criando um novo ticket, passe os dados do formulário para o modal
      const ticketData = {
        titulo: titulo,
        tipo: getValues("tipo"),
        grupo: getValues("grupo"),
        status: getValues("status"),
        prioridade: getValues("prioridade"),
        agente_id: getValues("agente_id"),
        dataVencimento:
          getValues("dataVencimento") || new Date().toISOString(),
      };

      setDadosAtividade({
        acao: "Novo",
        descricao: "",
        ticketData: ticketData,
        isNewTicket: true,
        id: 0,
      });
      setShowModalAtividade(true);
    } else {
      // Comportamento normal para tickets existentes
      setDadosAtividade({
        acao: "Novo",
        descricao: "",
        ticketData: {} as TicketInterface,
        isNewTicket: false,
        id: 0,
      });
      setShowModalAtividade(true);
    }
  };

  const handleCloseModal = () => {
    setShowModalAtividade(false);
  };

  const handleSubmitAtividade = async (dados: any) => {
    try {
      setLoading(true);

      // Verificar se é uma edição ou uma nova atividade
      if (dadosAtividade.acao === "Editar") {
        // Editar atividade existente
        const atividadeId = dadosAtividade.id;

        // Criar FormData para enviar a atividade com arquivos
        const formData = new FormData();
        formData.append("descricao", dados.descricao);

        // Adicionar arquivos se houver
        if (dados.arquivos && dados.arquivos.length > 0) {
          dados.arquivos.forEach((arquivo: File) => {
            formData.append("arquivos", arquivo);
          });
        }

        // Atualizar a atividade
        await endpointPrincipal.put(
          `/ticket-atividade/${atividadeId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.success("Atividade atualizada com sucesso");
      } else {
        // Criar nova atividade
        // Criar FormData para enviar a atividade com arquivos
        const formData = new FormData();

        formData.append("descricao", dados.descricao);
        formData.append("ticket_id", idticket!.toString());

        // Adicionar arquivos se houver
        if (dados.arquivos && dados.arquivos.length > 0) {
          dados.arquivos.forEach((arquivo: File) => {
            formData.append("arquivos", arquivo);
          });
        }

        // Criar a atividade
        await endpointPrincipal.post("/ticket-atividade", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Atividade incluída com sucesso");
      }

      // Recarregar as atividades do ticket
      const { data } = await endpointPrincipal.get(
        `tickets/${idticket}/atividades`
      );
      setAtividades(data.atividades || []);

      handleCloseModal();
    } catch (error: any) {
      console.error("Erro completo:", error);
      toast.error(
        `Erro ao ${
          dadosAtividade.acao === "Editar" ? "atualizar" : "incluir"
        } atividade: ${error.response?.data?.message || "Erro desconhecido"}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Função para baixar um arquivo
  const handleDownloadArquivo = async (arquivo: any) => {
    try {
      setLoading(true);
      const response = await endpointPrincipal.get(
        `/ticket-arquivos/${arquivo.id}/download`,
        {
          responseType: "blob",
        }
      );

      // Criar um URL para o blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Criar um link temporário e clicar nele para iniciar o download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", arquivo.nome);
      document.body.appendChild(link);
      link.click();

      // Limpar
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      toast.success(`Arquivo "${arquivo.nome}" baixado com sucesso`);
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error);
      toast.error(`Erro ao baixar arquivo: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Função para visualizar uma atividade
  const handleVisualizarAtividade = (atividade: any) => {
    setDadosAtividade({
      acao: "Visualizar",
      descricao: atividade.descricao,
      ticketData: {} as TicketInterface,
      isNewTicket: false,
      id: atividade.id,
    });
    setShowModalAtividade(true);
  };

  // Função para editar uma atividade
  const handleEditarAtividade = (atividade: any) => {
    setDadosAtividade({
      acao: "Editar",
      descricao: atividade.descricao,
      ticketData: {} as TicketInterface,
      isNewTicket: false,
      id: atividade.id,
    });
    setShowModalAtividade(true);
  };

  // Função para excluir uma atividade
  const handleExcluirAtividade = async (atividade: any) => {
    if (window.confirm(`Tem certeza que deseja excluir esta atividade?`)) {
      try {
        setLoading(true);
        await endpointPrincipal.delete(`/ticket-atividade/${atividade.id}`);

        // Recarregar as atividades do ticket
        const { data } = await endpointPrincipal.get(
          `tickets/${idticket}/atividades`
        );
        setAtividades(data.atividades || []);

        toast.success("Atividade excluída com sucesso");
      } catch (error: any) {
        console.error("Erro ao excluir atividade:", error);
        toast.error(
          `Erro ao excluir atividade: ${
            error.response?.data?.message || "Erro desconhecido"
          }`
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Um botão de download para cada arquivo da atividade
  const renderDownloads = (atividade: any) =>
    (atividade.arquivos || []).map((arquivo: any, index: number) => (
      <Tooltip key={arquivo.id ?? index} title={`Baixar arquivo: ${arquivo.nome}`} arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadArquivo(arquivo);
          }}
        >
          <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ));

  const podeAlterarAgenteGrupo = idticket !== "novo" || perfilUsuario === "agente";

  return (
    <>
      <Card>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <FormTitulo
            titulo="Tickets"
            acao={acao}
            complemento={idticket && idticket !== "novo" ? `#${idticket}` : undefined}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <CustomTextField
                  label="Contato *"
                  name="contato"
                  register={register}
                  rules={{ value: nomeUsuario }}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <CustomTextField
                  label="Assunto *"
                  name="assunto"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              {podeAlterarAgenteGrupo && (
                <Grid item xs={12} lg={4}>
                  <CustomSelect
                    label="Agente"
                    name="agente_id"
                    control={control}
                    opcoes={agenteOptions.map((agente) => ({
                      valor: agente.id_agente,
                      label: agente.nome,
                    }))}
                    disabled={isVisualizando || (idticket !== "novo" && perfilUsuario !== "agente")}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} lg={3}>
                <CustomSelect
                  label="Tipo"
                  name="tipo"
                  control={control}
                  opcoes={paraOpcoes(tipoOptions)}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CustomSelect
                  label="Status"
                  name="status"
                  control={control}
                  opcoes={paraOpcoes(statusOptions)}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CustomSelect
                  label="Prioridade *"
                  name="prioridade"
                  control={control}
                  opcoes={paraOpcoes(prioridadeOptions)}
                  disabled={isVisualizando}
                />
              </Grid>
              {podeAlterarAgenteGrupo && (
                <Grid item xs={12} sm={6} lg={3}>
                  <CustomSelect
                    label="Grupo"
                    name="grupo"
                    control={control}
                    opcoes={paraOpcoes(gruposOptions)}
                    disabled={isVisualizando || (idticket !== "novo" && perfilUsuario !== "agente")}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} lg={3}>
                <CustomTextField
                  label="Previsão de conclusão"
                  name="dataVencimento"
                  type="datetime-local"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <ActionToolbar
            onIncluir={handleOpenModal}
            disabledIncluir={isVisualizando}
            mostrarFiltrar={false}
          />
          <CustomTable
            columns={colunas}
            data={paginacaoAtividades.itensPagina}
            idField="id"
            onVisualizar={handleVisualizarAtividade}
            onEditar={isVisualizando ? undefined : handleEditarAtividade}
            onDelete={isVisualizando ? undefined : handleExcluirAtividade}
            acoesExtras={renderDownloads}
            paginaAtual={paginacaoAtividades.paginaAtual}
            totalPaginas={paginacaoAtividades.totalPaginas}
            setPaginaAtual={paginacaoAtividades.setPaginaAtual}
            maxHeight="45vh"
          />

          <FormActions onVoltar={() => navigate(-1)} visualizar={isVisualizando} />
        </Box>
      </Card>
      <ModalLoading show={loading} label="Processando..." />

      <ModalAtividade
        showModal={showModalAtividade}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAtividade}
        acao={dadosAtividade.acao}
        dadosDescricao={dadosAtividade.descricao}
        dadosId={dadosAtividade.id}
        ticketData={dadosAtividade.ticketData}
        isNewTicket={dadosAtividade.isNewTicket}
      />
    </>
  );
};

export default FormTicket;
